<script lang="ts">
	//Helpers
	import { real_id } from '../../../helpers/interface_helpers';
	import { getDeviceChannelByComponent } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	import { unlockPin } from 'src/helpers/pin_lock_helpers';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { hasControlRights } from 'src/helpers/user_helpers';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	//Design system components
	import {
		Text,
		DeviceButton,
		ControlsContainer,
		DeviceButtonV2,
		type DeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from '../../../types/Device';
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	//Components
	import PinUnlock from 'src/components/PinUnlock.svelte';

	let className = '';
	export { className as class };

	export let status,
		device: Device,
		/** The LED strip component id. Defaults to `ledstrip:0`. */
		component: string = 'ledstrip:0',
		isWide = false,
		isInSlot = false,
		useV2Components = false,
		controlName: string = null,
		icon: string = 'fa fa-power-off',
		deviceButtonV2Props: Partial<DeviceButtonV2PropsTypes> = {};

	let isLoading = false,
		pinLocked,
		showModal = false;

	const id = real_id(device.id);
	$: hasRights = hasControlRights(device);

	$: ison = status?.[component]?.on;
	$: tagline = ison
		? $format('label_action_turn_off', { default: 'turn off' })
		: $format('label_action_turn_on', { default: 'turn on' });

	const handleTurn = async () => {
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;
		if (pinLocked) {
			showModal = true;
			return;
		}
		if (isLoading || !status) return;
		isLoading = true;
		const on = !ison;
		const res: SettingRequest = await gen2Request(device, 'LEDStrip.Set', {
			id: getDeviceChannelByComponent(component),
			on,
		});
		if (res.isok) {
			ison = on;
			status[component] = { ...(status[component] || {}), on };
			devices_status.update_device_status({ id, msg: status });
		} else {
			onSettingsResponse(res, device, {
				success: '',
				danger: $format('error_global'),
			});
		}
		isLoading = false;
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleTurn();
	};

	const a11yProps = { timerAriaLabel: $format('label_turn_device', { default: 'Turn device' }) };
</script>

{#if status?.[component] != null}
	{#if isWide || isInSlot}
		<ControlsContainer
			class={classMap({ 'height-full': isInSlot })}
			color={useV2Components ? 'color-0' : undefined}
		>
			<svelte:fragment slot="first">
				{#if useV2Components}
					<DeviceButtonV2
						id="devicebutton-ledstrip-v2"
						{isWide}
						{isLoading}
						isActive={ison}
						size={isWide ? 'l' : 'm'}
						{icon}
						aria-label={$format('label_turn_device', { default: 'Turn device' })}
						{a11yProps}
						variant={'primary'}
						on:click={(e) => {
							e.stopPropagation();
							handleTurn();
						}}
						disabled={!status || !hasRights}
						class={className}
						{...deviceButtonV2Props}
					/>
				{:else}
					<DeviceButton
						id="devicebutton-ledstrip"
						{isWide}
						{isLoading}
						isActive={ison}
						size={isWide ? 'l' : 'm'}
						{icon}
						aria-label={$format('label_turn_device', { default: 'Turn device' })}
						{a11yProps}
						variant={'primary'}
						on:click={(e) => {
							e.stopPropagation();
							handleTurn();
						}}
						disabled={!status || !hasRights}
						class={className}
					/>
				{/if}
				<Text styleType="controls-large" class="mt-s text-bold">
					{capFirstChar(tagline)}
				</Text>
				{#if isInSlot}
					<Text styleType="tiny" class="mt-s">
						{capFirstChar(
							controlName ||
								$format('settings_pill_options_led_strip', { default: 'LED Strip' }),
						)}
					</Text>
				{/if}
			</svelte:fragment>
		</ControlsContainer>
	{:else if useV2Components}
		<DeviceButtonV2
			id="devicebutton-ledstrip-v2-compact"
			{isWide}
			{isLoading}
			isActive={ison}
			size={isWide ? 'l' : 'm'}
			{icon}
			aria-label={$format('label_turn_device', { default: 'Turn device' })}
			{a11yProps}
			variant={'primary'}
			on:click={(e) => {
				e.stopPropagation();
				handleTurn();
			}}
			disabled={!status || !hasRights}
			class={className}
			{...deviceButtonV2Props}
		/>
	{:else}
		<DeviceButton
			id="devicebutton-ledstrip-compact"
			{isWide}
			{isLoading}
			isActive={ison}
			size={isWide ? 'l' : 'm'}
			{icon}
			aria-label={$format('label_turn_device', { default: 'Turn device' })}
			{a11yProps}
			variant={'primary'}
			on:click={(e) => {
				e.stopPropagation();
				handleTurn();
			}}
			disabled={!status || !hasRights}
			class={className}
		/>
	{/if}
{/if}

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
